/**
 * Contextual shelf actions — the single source of truth for "what can I do
 * with this book" on the page (BookDetailModal/BookQuickPreviewPopover/
 * CoverDetailModal each used to duplicate a version of this). What renders
 * depends on real shelf state from useBookshelf(), not a generic action
 * list: a book already on a shelf never shows a redundant "Add to TBR".
 * Read/Borrow only ever appears when getReadableUrl() resolves to a real
 * Open Library / Internet Archive link — never a fake "Read Now".
 */

import React from 'react';
import { Bookmark, BookOpen, CheckCircle2, Heart, Share2, ExternalLink, BookMarked } from 'lucide-react';
import { Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useBookshelf } from '@/components/bookshelf';
import { toShelfBook } from '@/lib/bookModel';
import { getReadableUrl } from '@/services/openLibraryService';

function IconButton({ active, label, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cx(
        'grid h-9 w-9 shrink-0 place-items-center rounded-full border transition-colors',
        active ? 'border-coral bg-coral/10 text-coral' : 'border-line text-subtle hover:border-coral/40 hover:text-coral'
      )}
    >
      {children}
    </button>
  );
}

export default function BookActionsPanel({ book, isShelved, shelfBook }) {
  const { addBook, updateBook, toggleFavorite } = useBookshelf();
  const readableUrl = getReadableUrl(book);

  const handleAddToShelf = (shelfId) => {
    addBook(toShelfBook(book, shelfId), shelfId);
    toast(`Added "${book.title}" to ${shelfId === 'shelf_reading' ? 'Currently Reading' : 'TBR'}`);
  };

  const setStatus = (status, label) => {
    const updates = { status };
    if (status === 'reading' && shelfBook.status !== 'reading') updates.dateStarted = Date.now();
    if (status === 'finished') updates.dateFinished = Date.now();
    updateBook(shelfBook.id, updates);
    toast(`Marked "${book.title}" as ${label}`);
  };

  const handleShare = () => {
    navigator.clipboard?.writeText(window.location.href).catch(() => {});
    toast('Link copied to clipboard');
  };

  return (
    <div
      className={cx(
        'flex flex-wrap items-center justify-center gap-2',
        'fixed inset-x-0 bottom-16 z-30 border-t border-line bg-card p-3',
        'md:static md:z-auto md:justify-start md:border-0 md:bg-transparent md:p-0'
      )}
      data-testid="book-details-actions"
    >
      {!isShelved && (
        <>
          <Button variant="primary" icon={false} onClick={() => handleAddToShelf('shelf_tbr')} className="px-4 py-2 text-xs">
            <Bookmark className="h-3.5 w-3.5" /> Add to TBR
          </Button>
          <Button variant="secondary" icon={false} onClick={() => handleAddToShelf('shelf_reading')} className="px-4 py-2 text-xs">
            <BookOpen className="h-3.5 w-3.5" /> Currently Reading
          </Button>
        </>
      )}

      {isShelved && shelfBook.status === 'tbr' && (
        <>
          <Button variant="primary" icon={false} onClick={() => setStatus('reading', 'Currently Reading')} className="px-4 py-2 text-xs">
            <BookOpen className="h-3.5 w-3.5" /> Start Reading
          </Button>
          <Button variant="secondary" icon={false} onClick={() => setStatus('finished', 'Finished')} className="px-4 py-2 text-xs">
            <CheckCircle2 className="h-3.5 w-3.5" /> Mark as Read
          </Button>
        </>
      )}

      {isShelved && shelfBook.status === 'reading' && (
        <>
          <Button variant="primary" icon={false} onClick={() => setStatus('finished', 'Finished')} className="px-4 py-2 text-xs">
            <CheckCircle2 className="h-3.5 w-3.5" /> Mark as Read
          </Button>
          <Button variant="secondary" icon={false} onClick={() => setStatus('tbr', 'TBR')} className="px-4 py-2 text-xs">
            <Bookmark className="h-3.5 w-3.5" /> Move to TBR
          </Button>
        </>
      )}

      {isShelved && shelfBook.status === 'finished' && (
        <>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sage/30 px-4 py-2 text-xs font-semibold text-[#4f6249] dark:bg-sage/20 dark:text-[#b3c7aa]">
            <CheckCircle2 className="h-3.5 w-3.5" /> Finished
          </span>
          <Button variant="secondary" icon={false} onClick={() => setStatus('reading', 'Currently Reading')} className="px-4 py-2 text-xs">
            <BookOpen className="h-3.5 w-3.5" /> Read Again
          </Button>
        </>
      )}

      {isShelved && (
        <IconButton active={shelfBook.isFavorite} label={shelfBook.isFavorite ? 'Remove from favorites' : 'Add to favorites'} onClick={() => toggleFavorite(shelfBook.id)}>
          <Heart className={cx('h-4 w-4', shelfBook.isFavorite && 'fill-coral')} />
        </IconButton>
      )}

      <IconButton label="Share" onClick={handleShare}>
        <Share2 className="h-4 w-4" />
      </IconButton>

      {readableUrl && (
        <Button as="a" href={readableUrl} target="_blank" rel="noopener noreferrer" variant="secondary" icon={false} className="px-4 py-2 text-xs">
          <BookMarked className="h-3.5 w-3.5" />
          {book.ebookAccess === 'public' ? 'Read on Archive.org' : 'Borrow on Open Library'}
          <ExternalLink className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
